import { Button } from '@components/primitives/Button.js'
import { Input } from '@components/primitives/Input.js'
import { MoneyInput } from '@components/primitives/MoneyInput.js'
import { Select } from '@components/primitives/Select.js'
import { Modal } from '@components/primitives/Modal.js'
import { PADROES_CONSTRUCAO, MODULOS } from '@utils/constants.js'
import { TIPO_CONTRATACAO } from './types.js'
import type { ConfigPageState } from './useConfigPage.js'

type Props = Pick<
  ConfigPageState,
  | 'config'
  | 'editing'
  | 'saving'
  | 'editForm'
  | 'errors'
  | 'importando'
  | 'confirmImport'
  | 'importFile'
  | 'moduloReset'
  | 'confirmReset'
  | 'resettingProg'
  | 'confirmLimpar'
  | 'confirmLimpar2'
  | 'digitouNome'
  | 'limpando'
  | 'cancelEdit'
  | 'setField'
  | 'setOrcamentoTotal'
  | 'handleSave'
  | 'handleConfirmarImport'
  | 'handleResetarProgresso'
  | 'handleLimparTodos'
  | 'cancelImport'
  | 'closeLimparStep1'
  | 'closeLimparStep2'
  | 'setConfirmReset'
  | 'setConfirmLimpar2'
  | 'setDigitouNome'
>

export function ConfigModals({
  config,
  editing,
  saving,
  editForm,
  errors,
  importando,
  confirmImport,
  importFile,
  moduloReset,
  confirmReset,
  resettingProg,
  confirmLimpar,
  confirmLimpar2,
  digitouNome,
  limpando,
  cancelEdit,
  setField,
  setOrcamentoTotal,
  handleSave,
  handleConfirmarImport,
  handleResetarProgresso,
  handleLimparTodos,
  cancelImport,
  closeLimparStep1,
  closeLimparStep2,
  setConfirmReset,
  setConfirmLimpar2,
  setDigitouNome,
}: Props) {
  return (
    <>
      <Modal
        open={editing}
        title='Editar Dados da Obra'
        size='md'
        onClose={cancelEdit}
        actions={
          <>
            <Button variant='ghost' size='sm' onClick={cancelEdit} disabled={saving}>
              Cancelar
            </Button>
            <Button size='sm' onClick={handleSave} loading={saving}>
              Salvar Alterações
            </Button>
          </>
        }
      >
        {editForm && (
          <div className='cf3-config-edit-form'>
            <Input
              name='nome'
              label='Nome da Obra'
              required
              value={editForm.nome}
              onChange={setField('nome')}
              error={errors.nome ?? ''}
            />
            <Input
              name='dataInicio'
              label='Data de Início'
              type='date'
              value={editForm.dataInicio}
              onChange={setField('dataInicio')}
            />
            <Input
              name='areaTotal'
              label='Área Total (m²)'
              type='number'
              suffix='m²'
              required
              value={String(editForm.areaTotal)}
              onChange={setField('areaTotal')}
              error={errors.areaTotal ?? ''}
            />
            <Select
              name='padraoConstrucao'
              label='Padrão de Construção'
              required
              value={editForm.padraoConstrucao}
              onChange={setField('padraoConstrucao')}
              options={PADROES_CONSTRUCAO}
              error={errors.padraoConstrucao ?? ''}
            />
            <MoneyInput
              label='Orçamento Total'
              required
              value={editForm.orcamentoTotal}
              onChange={setOrcamentoTotal}
              error={errors.orcamentoTotal ?? ''}
            />
            <Select
              name='tipoContratacao'
              label='Tipo de Contratação'
              placeholder='Selecione...'
              value={editForm.tipoContratacao}
              onChange={setField('tipoContratacao')}
              options={[...TIPO_CONTRATACAO]}
            />
          </div>
        )}
      </Modal>

      <Modal
        open={confirmImport}
        title='Restaurar Backup'
        size='sm'
        onClose={cancelImport}
        actions={
          <>
            <Button variant='ghost' size='sm' onClick={cancelImport} disabled={importando}>
              Cancelar
            </Button>
            <Button size='sm' onClick={handleConfirmarImport} loading={importando}>
              Sim, restaurar
            </Button>
          </>
        }
      >
        <p className='cf3-config-modal-text'>
          Isso irá <strong>apagar todos os dados atuais</strong> e substituí-los pelo conteúdo do
          arquivo <strong>{importFile?.name}</strong>. Esta ação não pode ser desfeita.
        </p>
        <p className='cf3-config-modal-text cf3-config-modal-text--sub'>
          Certifique-se de que o arquivo é um backup válido exportado por este app.
        </p>
      </Modal>

      <Modal
        open={confirmReset}
        title='Reiniciar progresso'
        size='sm'
        onClose={() => setConfirmReset(false)}
        actions={
          <>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setConfirmReset(false)}
              disabled={resettingProg}
            >
              Cancelar
            </Button>
            <Button size='sm' onClick={handleResetarProgresso} loading={resettingProg}>
              Confirmar reinício
            </Button>
          </>
        }
      >
        <p className='cf3-config-modal-text'>
          {moduloReset ? (
            <>
              Tem certeza que deseja reiniciar o progresso do módulo{' '}
              <strong>&quot;{MODULOS.find((m) => m.id === moduloReset)?.titulo}&quot;</strong>?
            </>
          ) : (
            <>
              Tem certeza que deseja reiniciar o progresso de <strong>todos os módulos</strong>?
            </>
          )}
        </p>
        <p className='cf3-config-modal-text cf3-config-modal-text--sub'>
          Lançamentos financeiros e documentos não serão afetados.
        </p>
      </Modal>

      <Modal
        open={confirmLimpar && !confirmLimpar2}
        title='Limpar Todos os Dados'
        size='sm'
        onClose={closeLimparStep1}
        actions={
          <>
            <Button variant='ghost' size='sm' onClick={closeLimparStep1}>
              Cancelar
            </Button>
            <Button size='sm' onClick={() => setConfirmLimpar2(true)}>
              Continuar
            </Button>
          </>
        }
      >
        <p className='cf3-config-modal-text'>
          Esta ação vai apagar <strong>permanentemente</strong> todos os dados: configuração da
          obra, progresso, lançamentos financeiros, documentos e diário de bordo.
        </p>
        <p className='cf3-config-modal-text cf3-config-modal-text--sub'>
          Recomendamos fazer um backup antes de continuar.
        </p>
      </Modal>

      <Modal
        open={confirmLimpar && confirmLimpar2}
        title='Confirmação Final'
        size='sm'
        onClose={closeLimparStep2}
        actions={
          <>
            <Button variant='ghost' size='sm' onClick={closeLimparStep2} disabled={limpando}>
              Cancelar
            </Button>
            <Button
              size='sm'
              onClick={handleLimparTodos}
              loading={limpando}
              disabled={digitouNome !== config?.nome}
            >
              Apagar tudo
            </Button>
          </>
        }
      >
        <p className='cf3-config-modal-text cf3-config-modal-text--gap'>
          Digite o nome da obra para confirmar: <strong>{config?.nome}</strong>
        </p>
        <Input
          name='confirma-nome'
          label='Nome da obra'
          value={digitouNome}
          onChange={(e) => setDigitouNome(e.target.value)}
          placeholder={config?.nome ?? ''}
        />
      </Modal>
    </>
  )
}
